<template>
  <div class="customersSatisfactionReport">
    <Loading v-if="loading" />
    <div class="customersSatisfactionReport__chooseDate">
      <div class="customersSatisfactionReport__chooseDate__container">
        <div class="chooseDate-date-inputs">
          <div class="input">
            <Calendar
              :key="calendarStartKey"
              type="date"
              format="DD/MM/YYYY"
              name="startTime"
              :value="firstDay"
              :message="$t('errorDate')"
              @change="changeDateStartTime"
            />
          </div>
          <img class="date-img" src="@/assets/icons/Hyphen.svg" alt="Hyphen" />
          <div class="input">
            <Calendar
              :key="calendarEndKey"
              type="date"
              format="DD/MM/YYYY"
              name="endTime"
              :value="today"
              @change="changeDateEndTime"
            />
          </div>
        </div>
        <Dropdown
          class="customersSatisfactionReport__thisPeriod"
          :selected-option="getOptionThisPeriod.selectedOption"
          :options="getOptionThisPeriod.options"
          @select-option="selectOptionThisPeriod"
        />
      </div>
      <p v-if="!isDateCorrect" class="date-error">{{ $t('errorDate') }}</p>
    </div>
    <div class="customersSatisfactionReport__diagrams">
      <div
        v-if="
          dataLineChart &&
          dataLineChart.labels.length &&
          (!dataLineChart.datasets[0].data.every((value) => !value) ||
            !dataLineChart.datasets[1].data.every((value) => !value))
        "
        :class="{
          'line-diagram': true,
          'line-diagram-long': dataPieChart.datasets[0].data.every(
            (value) => !value
          )
        }"
      >
        <LineChart
          :id="'lineChartTwoLegends'"
          :data-line-chart="dataLineChart"
        />
        <div class="satisfaction-periods">
          <div class="thisPeriod">
            <p>{{ $t('thisPeriod') }}</p>
          </div>
          <div class="otherPeriod">
            <Dropdown
              class="customersSatisfactionReport__otherPeriod"
              :selected-option="getOptionOtherPeriod.selectedOption"
              :options="getOptionOtherPeriod.options"
              @select-option="selectOptionOtherPeriod"
            />
          </div>
        </div>
      </div>
      <div
        v-if="
          dataPieChart &&
          dataPieChart.labels.length &&
          !loading &&
          !dataPieChart.datasets[0].data.every((value) => !value)
        "
        class="pie-diagram"
      >
        <PieChart
          :data-pie-chart="dataPieChart"
          :style="stylePieChart"
          @segment-click="handleSegmentClick"
        />
      </div>
    </div>
    <Description
      :data="dailyStatistics?.currentPharmacyStats.textData"
      :pickup-boxes="entityNames?.pickupBoxes"
      :options="pharmacy?.feedbackTemplate.options_screen.options"
    />
    <Amount :data="dailyStatistics?.currentPharmacyStats.amountData" />
    <div class="container">
      <div
        v-if="
          dataBarChart?.labels?.length &&
          (!dataBarChart.datasets[0].data.every((value) => !value) ||
            !dataBarChart.datasets[1].data.every((value) => !value) ||
            !dataBarChart.datasets[2].data.every((value) => !value))
        "
        class="customersSatisfactionReport__barChart"
      >
        <BarChart
          :id="'barChart'"
          :data-bar-chart="dataBarChart"
          :options-bar-chart="optionsBarChart"
          :style="styleBarChart"
        />
      </div>
    </div>
    <div class="container">
      <div class="customersSatisfactionReport__generalSatisfaction">
        <HeatmapChart :heatmap-data="filteredData" />
      </div>
    </div>

    <div class="container">
      <div
        v-if="
          dataBarChart?.labels?.length &&
          (!dataBarChart.datasets[0].data.every((value) => !value) ||
            !dataBarChart.datasets[1].data.every((value) => !value) ||
            !dataBarChart.datasets[2].data.every((value) => !value))
        "
        class="customersSatisfactionReport__barChart"
      >
        <BarChart
          :id="'barChartAmount'"
          :data-bar-chart="dataBarChartAmount"
          :options-bar-chart="optionsBarChartAmount"
          :style="styleBarChart"
        />
      </div>
    </div>

    <CustomersFeedbackBlock
      v-if="showBlockCustomersFeedbackBlock"
      :data="dailyStatistics?.currentPharmacyStats.comments"
    />
  </div>

  <AllFeedback
    :feedback="feedback"
    :selected-key="selectedKey"
    :comment-key="commentKey"
    :show-modal-all-feedback="showModalAllFeedback"
    @close-modal="showModalAllFeedback = false"
  />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';
import { format, subMonths } from 'date-fns';

import Loading from '@/views/Loading.vue';
import LineChart from '@/components/UI/LineChart/LineChart.vue';
import PieChart from '@/components/UI/PieChart/PieChart.vue';
import HeatmapChart from '@/components/UI/HeatmapChart/HeatmapChart.vue';

import AllFeedback from './AllFeedback/AllFeedback.vue';
import Description from './Description/Description.vue';
import Amount from './Amount/Amount.vue';
import CustomersFeedbackBlock from './CustomersFeedbackBlock/CustomersFeedbackBlock.vue';

import router from '@/router';

import {
  activeOptions,
  activeOtherPeriodOptions,
  colorScale,
  getLabelsMap,
  defaultDataLineChart,
  defaultDataPieChart,
  defaultDataBarChart,
  defaultDataBarChartAmount,
  optionsBarChart,
  optionsBarChartAmount
} from './configs';

import { InputFileEvent, Option } from '@/types';
import { LineChartDataset } from '@/store/modules/pharmacy/types';

@Options({
  name: 'CustomersSatisfactionReport',
  props: ['pharmacy'],
  components: {
    Loading,
    LineChart,
    PieChart,
    HeatmapChart,
    AllFeedback,
    Description,
    Amount,
    CustomersFeedbackBlock
  },
  data() {
    return {
      today: format(
        new Date(new Date().setDate(new Date().getDate() - 1)).setHours(
          23,
          59,
          59,
          999
        ),
        'dd/MM/yyyy HH:mm:ss'
      ),
      firstDay: format(
        new Date(
          new Date(new Date().setDate(new Date().getDate() - 7)).setHours(
            0,
            0,
            0,
            0
          )
        ),
        'dd/MM/yyyy HH:mm:ss'
      ),
      calendarStartKey: 0,
      calendarEndKey: 0,
      visibleDayIndexes: [] as number[],
      height: 340,
      heightPieChart: 300,
      heightBarChart: 360,
      widthBarChart: 1330,
      filteredData: [],
      feedback: null,
      selectedKey: '',
      commentKey: '',
      colorScale,
      getLabelsMap,
      loading: false,
      changeOptions: false,
      showModalAllFeedback: false,
      dataLineChart: defaultDataLineChart,
      dataPieChart: defaultDataPieChart,
      dataBarChart: defaultDataBarChart,
      dataBarChartAmount: defaultDataBarChartAmount,
      optionsBarChart: optionsBarChart,
      optionsBarChartAmount: optionsBarChartAmount,
      selectedThisPeriod: 'week',
      selectedOtherPeriod: 'prevPeriod',
      params: {
        pharmacyId: '',
        startTime: new Date(
          new Date().setDate(new Date().getDate() - 7)
        ).setHours(0, 0, 0, 0),
        endTime: new Date(
          new Date().setDate(new Date().getDate() - 1)
        ).setHours(23, 59, 59, 999),
        thisPeriod: 'week',
        otherPeriod: 'prevPeriod'
      },
      getOptionThisPeriod: {
        selectedOption: {
          value: 'week',
          label: this.$t('week')
        },
        options: activeOptions(this.$t)
      },
      getOptionOtherPeriod: {
        selectedOption: {
          value: 'prevPeriod',
          label: this.$t('prevPeriod')
        },
        options: activeOtherPeriodOptions(this.$t)
      }
    };
  },
  created() {
    if (this.pharmacy) {
      this.params = {
        pharmacyId: this.pharmacy.id,
        startTime: this.params.startTime,
        endTime: this.params.endTime,
        otherPeriod: this.selectedOtherPeriod
      };
    }
  },
  computed: {
    ...mapState('orders', ['dailyStatistics']),
    ...mapState('entityNames', ['entityNames']),
    isDateCorrect() {
      if (this.isDatePresent) {
        return this.params.endTime >= this.params.startTime;
      }

      return true;
    },
    isDatePresent() {
      return this.params && this.params.startTime && this.params.endTime;
    },
    showBlockCustomersFeedbackBlock() {
      const comments = this.dailyStatistics?.currentPharmacyStats.comments;

      if (!comments) return false;

      const hasAnyComment = Object.values(comments).some(
        (arr) => Array.isArray(arr) && arr.length > 0
      );

      return hasAnyComment;
    },
    stylePieChart() {
      return {
        height: `${this.heightPieChart}px`
      };
    },
    styleBarChart() {
      return {
        height: `${this.heightBarChart}px`,
        width: `${this.widthBarChart}px`
      };
    }
  },
  methods: {
    ...mapActions('orders', ['getCustomersSatisfaction']),
    ...mapActions('entityNames', ['getEntityNames']),
    async show() {
      await this.getEntityNames();
    },
    async initData() {
      this.loading = true;

      const defaultParams = { ...this.params };

      if (
        defaultParams.startTime &&
        defaultParams.endTime &&
        this.isDateCorrect
      ) {
        await this.getCustomersSatisfaction(defaultParams);

        this.show();

        this.initVisibleDays();
        this.initCustomersSatisfactionData();
        this.initPieChartData();
        this.initGroupedChartData();
        this.initBarChartAmount();
        this.initFilteredData();

        this.loading = false;
      }
    },
    selectOptionThisPeriod(option: Option) {
      this.getOptionThisPeriod.selectedOption = option;
    },
    selectOptionOtherPeriod(option: Option) {
      this.getOptionOtherPeriod.selectedOption = option;

      this.selectedOtherPeriod = this.getOptionOtherPeriod.selectedOption.value;
    },
    handleSegmentClick({ key }: { key: string }) {
      this.loading = true;

      this.commentKey = key;
      this.selectedKey = this.getLabelsMap()[key];
      this.feedback =
        this.dailyStatistics.currentPharmacyStats.comments[key] || [];

      this.loading = false;
      this.showModalAllFeedback = true;
    },
    getRawLabels() {
      return Object.keys(
        this.dailyStatistics.currentPharmacyStats.lineGraphicsData.thisPeriod
      ).map((date: string) => date);
    },
    getLabels() {
      return this.pickVisible(this.getRawLabels());
    },
    // Keeps only the entries whose day survived the closed-day filter. Every
    // chart on this page is indexed by the same list of days, so they all stay
    // aligned with the labels.
    pickVisible(values: unknown[]) {
      const list = Array.isArray(values) ? values : Object.values(values || {});

      return this.visibleDayIndexes.map((index: number) => list[index]);
    },
    // The label format is decided by the backend, so parse the shapes it can
    // realistically send and give up rather than guess.
    parseLabelDate(label: string): Date | null {
      if (typeof label !== 'string') return null;

      const value = label.trim();

      const iso = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (iso) {
        return new Date(+iso[1], +iso[2] - 1, +iso[3]);
      }

      const withYear = value.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
      if (withYear) {
        return new Date(+withYear[3], +withYear[2] - 1, +withYear[1]);
      }

      // `dd/MM` carries no year — take the one that lands inside the selected
      // range, which also covers a range spanning New Year.
      const dayMonth = value.match(/^(\d{1,2})[./-](\d{1,2})$/);
      if (dayMonth) {
        const start = new Date(this.params.startTime);
        const end = new Date(this.params.endTime);

        for (
          let year = start.getFullYear();
          year <= end.getFullYear();
          year++
        ) {
          const candidate = new Date(year, +dayMonth[2] - 1, +dayMonth[1]);

          if (
            candidate.valueOf() >= new Date(start).setHours(0, 0, 0, 0) &&
            candidate.valueOf() <= this.params.endTime
          ) {
            return candidate;
          }
        }
      }

      return null;
    },
    isPharmacyClosedOn(date: Date): boolean {
      const workingHours = this.pharmacy?.workingHours;

      if (!workingHours) return false;

      const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      const day = workingHours[dayKeys[date.getDay()]];

      if (!day) return false;

      return day.closed === true || (!day.from && !day.to);
    },
    // A day counts as having data when any feedback or pickup box activity was
    // recorded for it, whatever the pharmacy's opening hours say.
    hasDataForDay(index: number): boolean {
      const stats = this.dailyStatistics?.currentPharmacyStats;

      if (!stats) return true;

      // A satisfaction score only proves activity when it is above zero — an
      // empty day is reported as either null or a flat 0 depending on the
      // backend, and a genuine 0% day is caught by the counts below.
      const satisfaction = Object.values(
        stats.lineGraphicsData?.thisPeriod || {}
      )[index];

      if (Number(satisfaction) > 0) return true;

      const grouped = stats.groupedChartData?.datasets || [];
      const hasGrouped = grouped.some(
        (dataset: { data: number[] }) =>
          Number(Object.values(dataset.data || {})[index]) > 0
      );

      if (hasGrouped) return true;

      const amount = Object.values(
        stats.barChartSatisfactionData?.datasets?.data || {}
      )[index];

      return Number(amount) > 0;
    },
    // Drop the days the pharmacy is closed, unless something was actually
    // recorded on them — those gaps are what the charts should not show.
    initVisibleDays() {
      const labels = this.getRawLabels();

      this.visibleDayIndexes = labels.reduce(
        (kept: number[], label: string, index: number) => {
          if (this.hasDataForDay(index)) {
            kept.push(index);
            return kept;
          }

          const date = this.parseLabelDate(label);

          // An unparseable label is kept — never hide a day we cannot place.
          if (!date || !this.isPharmacyClosedOn(date)) {
            kept.push(index);
          }

          return kept;
        },
        []
      );
    },
    initCustomersSatisfactionData() {
      const stats = this.dailyStatistics.currentPharmacyStats.lineGraphicsData;

      const dataThisPeriod = this.pickVisible(Object.values(stats.thisPeriod));
      const dataOtherPeriod = this.pickVisible(
        Object.values(stats.otherPeriod)
      );

      this.dataLineChart = {
        ...this.dataLineChart,
        labels: this.getLabels(),
        datasets: this.dataLineChart.datasets.map((item: LineChartDataset) => {
          switch (item.label) {
            case 'thisPeriod':
              return { ...item, data: dataThisPeriod };
            case 'otherPeriod':
              return { ...item, data: dataOtherPeriod };
            default:
              return item;
          }
        })
      };
    },
    initPieChartData() {
      const totals =
        this.dailyStatistics.currentPharmacyStats.pieChartData || {};

      const order = Object.keys(this.getLabelsMap());

      const data = order.map((key) => {
        const v = totals[key];

        return v;
      });

      this.dataPieChart = {
        labels: order.map((key) => this.getLabelsMap()[key]),
        datasets: [
          {
            ...this.dataPieChart.datasets[0],
            data
          }
        ]
      };
    },
    initGroupedChartData() {
      if (
        this.dailyStatistics.currentPharmacyStats.lineGraphicsData.thisPeriod
      ) {
        const stats =
          this.dailyStatistics.currentPharmacyStats.groupedChartData;

        this.dataBarChart = {
          ...stats,
          datasets: [
            {
              ...this.dataBarChart.datasets[0],
              label: this.$t('angry'),
              data: this.pickVisible(Object.values(stats.datasets[0].data))
            },
            {
              ...this.dataBarChart.datasets[1],
              label: this.$t('ok'),
              data: this.pickVisible(Object.values(stats.datasets[1].data))
            },
            {
              ...this.dataBarChart.datasets[2],
              label: this.$t('happy'),
              data: this.pickVisible(Object.values(stats.datasets[2].data))
            }
          ],
          labels: this.getLabels()
        };
      }
    },
    initBarChartAmount() {
      if (
        this.dailyStatistics.currentPharmacyStats.lineGraphicsData.thisPeriod
      ) {
        const getColorByPercent = (percent: number): string => {
          const step = Math.round(percent / 10);
          return this.colorScale[Math.min(step, this.colorScale.length - 1)];
        };

        const stats =
          this.dailyStatistics.currentPharmacyStats.barChartSatisfactionData
            .datasets;

        this.dataBarChartAmount = {
          labels: this.getLabels(),
          datasets: [
            {
              ...this.dataBarChartAmount.datasets,
              data: this.pickVisible(stats.data),
              backgroundColor: this.pickVisible(stats.satisfactionPercent).map(
                (percent: number) => getColorByPercent(percent)
              )
            }
          ]
        };
      }
    },
    initFilteredData() {
      this.filteredData =
        this.dailyStatistics.currentPharmacyStats &&
        this.dailyStatistics.currentPharmacyStats.satisfactionRaw
          ? this.dailyStatistics.currentPharmacyStats.satisfactionRaw
          : [];
    },
    changeDateStartTime(e: InputFileEvent) {
      const { name } = e.target;
      const [day, month, year] = e.target.value.split('/');

      const dateObj = new Date(`${month}/${day}/${year}`);
      dateObj.setHours(0, 0, 0, 0);

      const date = dateObj.getTime();
      this.params.startTime = date;

      this.firstDay = format(dateObj, 'dd/MM/yyyy');
      this.calendarStartKey++;

      const now = new Date();
      const defaultWeekStart = new Date().setDate(now.getDate() - 7);
      const defaultMonthStart = new Date(
        now.getFullYear(),
        now.getMonth(),
        1
      ).getTime();
      const defaultFullMonthStart = this.getFullMonthStart().valueOf();

      if (this.getOptionThisPeriod.selectedOption) {
        if (
          (this.getOptionThisPeriod.selectedOption.value === 'week' &&
            date !== defaultWeekStart) ||
          (this.getOptionThisPeriod.selectedOption.value === 'month' &&
            date !== defaultMonthStart) ||
          (this.getOptionThisPeriod.selectedOption.value === 'fullMonth' &&
            date !== defaultFullMonthStart)
        ) {
          this.getOptionThisPeriod.selectedOption = { value: '', label: '' };
        }
      }

      this.params = {
        ...this.params,
        [name]: dateObj.valueOf()
      };
    },
    changeDateEndTime(e: InputFileEvent) {
      const { name } = e.target;
      const [day, month, year] = e.target.value.split('/');

      const dateObj = new Date(`${month}/${day}/${year}`);
      dateObj.setHours(23, 59, 59, 999);

      const date = dateObj.getTime();
      this.params.endTime = date;

      this.today = format(dateObj, 'dd/MM/yyyy');
      this.calendarEndKey++;

      const now = new Date();
      const defaultEnd = now.getTime();

      if (this.getOptionThisPeriod.selectedOption && date !== defaultEnd) {
        this.getOptionThisPeriod.selectedOption = { value: '', label: '' };
      }

      this.params = {
        ...this.params,
        [name]: dateObj.valueOf()
      };
    },
    // Start of the rolling full month: the same day one month back, e.g. the
    // 26th of August looks back to the 26th of July. date-fns clamps a day the
    // shorter month does not have (31 March → 28/29 February).
    getFullMonthStart(): Date {
      const start = subMonths(new Date(), 1);
      start.setHours(0, 0, 0, 0);

      return start;
    },
    closeTimePanel() {
      this.initChartsData();
    },
    goToSettings() {
      router.push({ name: 'SettingsCustomersFeedback' });
    }
  },
  watch: {
    params() {
      if (this.isDateCorrect) {
        this.dataLineChart = { ...defaultDataLineChart };
        this.dataPieChart = { ...defaultDataPieChart };
        this.dataBarChart = { ...defaultDataBarChart };
        this.dataBarChartAmount = { ...defaultDataBarChartAmount };

        this.initData(this.params);
      }
    },
    selectedThisPeriod() {
      this.params = {
        ...this.params,
        thisPeriod: this.selectedThisPeriod
      };
    },
    selectedOtherPeriod() {
      this.params = {
        ...this.params,
        otherPeriod: this.selectedOtherPeriod
      };
    },
    'getOptionThisPeriod.selectedOption'(option: Option) {
      if (!option) return;

      const now = new Date();
      const end = new Date(
        new Date().setDate(new Date().getDate() - 1)
      ).setHours(23, 59, 59, 999);

      if (option.value === 'week') {
        const firstOfWeek = new Date(
          new Date().setDate(new Date().getDate() - 7)
        ).setHours(0, 0, 0, 0);

        this.params = {
          ...this.params,
          startTime: firstOfWeek,
          endTime: end
        };

        this.firstDay = format(
          new Date().setDate(now.getDate() - 7),
          'dd/MM/yyyy'
        );

        this.today = format(end, 'dd/MM/yyyy');
      }

      if (option.value === 'month') {
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        this.params = {
          ...this.params,
          startTime: firstOfMonth.getTime(),
          endTime: end
        };

        this.firstDay = format(firstOfMonth, 'dd/MM/yyyy');
        this.today = format(end, 'dd/MM/yyyy');
      }

      // Full month runs up to today rather than yesterday, so the range reads
      // as a whole month: the 26th of July through the 26th of August.
      if (option.value === 'fullMonth') {
        const fullMonthStart = this.getFullMonthStart();
        const endOfToday = new Date().setHours(23, 59, 59, 999);

        this.params = {
          ...this.params,
          startTime: fullMonthStart.valueOf(),
          endTime: endOfToday
        };

        this.firstDay = format(fullMonthStart, 'dd/MM/yyyy');
        this.today = format(endOfToday, 'dd/MM/yyyy');
      }

      this.calendarStartKey++;
      this.calendarEndKey++;

      this.selectedThisPeriod = option.value;
    },
    pharmacy() {
      if (this.pharmacy) {
        this.params = {
          pharmacyId: this.pharmacy.id,
          startTime: this.params.startTime,
          endTime: this.params.endTime,
          otherPeriod: this.selectedOtherPeriod
        };
      }
    }
  }
})
export default class CustomersSatisfactionReport extends Vue {}
</script>

<style lang="scss" scoped>
.customersSatisfactionReport {
  .customersSatisfactionReport__chooseDate {
    margin: 32px 24px;
    .customersSatisfactionReport__chooseDate__container {
      display: flex;
      align-content: center;
      justify-content: flex-start;
      .chooseDate-date-inputs {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        margin-right: 24px;
        .formkit-outer {
          height: 42px;
          margin-bottom: 0 !important;
          .formkit-wrapper {
            height: 42px;
            .formkit-inner {
              height: 42px;
            }
          }
        }
        .input {
          position: relative;
          margin-bottom: 0;
          z-index: 2;
          .input-img {
            position: absolute;
            top: 12px;
            right: 12px;
            z-index: 0;
          }
        }
        .date-img {
          margin: 0 8px;
        }
      }
      .customersSatisfactionReport__thisPeriod {
        width: 150px;
      }
    }
    .date-error {
      color: $red;
      font-size: $fourteenSize;
      margin-top: 10px;
    }
  }
  .customersSatisfactionReport__diagrams {
    padding: 0 24px;
    display: flex;
    justify-content: center;
    @media (max-width: 1440px) {
      justify-content: flex-start;
    }
    .line-diagram {
      position: relative;
      width: 723px;
      height: 350px;
      padding: 20px;
      padding-left: 46px;
      border: 1px solid $border;
      border-radius: 8px;
      margin-right: 20px;
      @media (max-width: 1280px) {
        width: 593px;
        height: 300px;
      }
      &.line-diagram-long {
        max-width: 1099px;
        width: 100%;
        padding-left: 60px;
        margin-right: 0;
        @media (max-width: 1280px) {
          max-width: 941px;
        }
      }
    }
    .pie-diagram {
      width: 330px;
      height: 350px;
      padding: 20px;
      border: 1px solid $border;
      border-radius: 8px;
      @media (max-width: 1280px) {
        width: 307px;
        height: 300px;
      }
    }
    .satisfaction-periods {
      position: absolute;
      top: 14%;
      right: 55px;
      transform: translate(0, -50%);
      width: 330px;
      height: 31px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      .thisPeriod {
        position: relative;
        p {
          display: inline-block;
          width: max-content;
          padding: 8px;
          border: 1px solid $border;
          border-radius: 8px;
          color: $lineColor;
          font-size: 14px;
        }
        &::before {
          position: absolute;
          content: '';
          top: 50%;
          right: 85%;
          transform: translate(-50%, -50%);
          width: 50px;
          height: 2px;
          background-color: $lineColor;
        }
      }
      .otherPeriod {
        height: 31px;
        .customersSatisfactionReport__otherPeriod {
          height: 30px;
          width: 160px;
          .dropdown__head {
            height: 30px;
          }
        }
        &::before {
          position: absolute;
          content: '- - - - - -';
          top: 50%;
          right: 44%;
          transform: translate(-50%, -50%);
          width: 50px;
          color: $dottedlineColor;
        }
      }
    }
  }
  .container {
    display: flex;
    justify-content: center;
    @media (max-width: 1440px) {
      display: block;
    }
    @media (max-width: 1366px) {
      max-width: 1068px;
    }
    @media (max-width: 1280px) {
      max-width: 981px;
    }
    .customersSatisfactionReport__barChart {
      max-width: 1140px;
      width: 100%;
      border: 1px solid $border;
      border-radius: 8px;
      padding: 20px;
      margin: 24px;
    }
  }
  .container {
    .customersSatisfactionReport__generalSatisfaction {
      max-width: 1140px;
      width: 100%;
      border: 1px solid $border;
      border-radius: 8px;
      padding: 20px;
      margin: 24px;
    }
  }
}
</style>
